import React, { createContext, use, useContext, useState } from "react";
import { client, unwrap } from "./client";
import { resetGardenResource } from "./garden-resource";

export type AuthUser = { id: string; email: string };

type AuthContextType = {
  user: AuthUser | null;
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  requestPasswordReset: (email: string) => Promise<void>;
};

const AuthContext = createContext<AuthContextType | null>(null);

// One-shot session probe on app boot. 401 (logged out) resolves to null rather
// than throwing, so the unauthenticated UI can render.
const sessionProbe = probeSession();
async function probeSession(): Promise<AuthUser | null> {
  try {
    const res = await client.api.auth.me.$get();
    if (!res.ok) return null;
    return (await res.json()) as AuthUser;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: Readonly<{ children: React.ReactNode }>) {
  const initial = use(sessionProbe); // suspends until the boot probe resolves
  const [user, setUser] = useState<AuthUser | null>(initial);

  async function login(email: string, password: string) {
    const u = await unwrap(client.api.auth.login.$post({ json: { email, password } }));
    resetGardenResource(); // fetch this account's garden, not a prior session's
    setUser(u);
  }

  async function signup(email: string, password: string) {
    const u = await unwrap(client.api.auth.signup.$post({ json: { email, password } }));
    resetGardenResource();
    setUser(u);
  }

  async function logout() {
    await client.api.auth.logout.$post();
    resetGardenResource();
    setUser(null);
  }

  async function requestPasswordReset(email: string) {
    await unwrap(client.api.auth["request-reset"].$post({ json: { email } }));
  }

  return (
    <AuthContext.Provider value={{ user, login, signup, logout, requestPasswordReset }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
